// ============================================
// PaymentMethodForm Component
// ============================================
// Securely add payment methods using Stripe Elements
// Displays saved payment methods
// ============================================

import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import AppFooter from '../AppFooter';
import { FaHome, FaReceipt, FaCreditCard } from 'react-icons/fa';
import './PaymentMethodForm.css';

// Initialize Stripe with publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Payment Form Component (wrapped in Elements provider)
const PaymentForm = ({ actualTenantId, clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm the setup
      const { error: stripeError } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
      } else {
        // Success - payment method saved
        onSuccess();
      }
    } catch (err) {
      setError('An error occurred while saving your payment method.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <PaymentElement />
      
      {error && <div className="payment-error">{error}</div>}
      
      <div className="form-actions">
        <button 
          type="button" 
          className="btn-cancel" 
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn-submit" 
          disabled={!stripe || processing}
        >
          {processing ? 'Processing...' : 'Save Payment Method'}
        </button>
      </div>
    </form>
  );
};

// Main Component
const PaymentMethodForm = ({ tenantId }) => {
  const navigate = useNavigate();
  const params = useParams();
  const actualTenantId = tenantId || params.tenant;
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPaymentMethods();
  }, [actualTenantId]);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/billing/payment-methods/${actualTenantId}`);
      setPaymentMethods(res.data.paymentMethods);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      setError(null);
      const res = await axios.post(`/api/billing/payment-method/setup-intent/${actualTenantId}`);
      setClientSecret(res.data.clientSecret);
      setShowAddForm(true);
    } catch (err) {
      console.error('Error creating setup intent:', err);
      setError('Failed to initialize payment form');
    }
  };

  const handlePaymentSuccess = async () => {
    setShowAddForm(false);
    setClientSecret('');
    await fetchPaymentMethods();
    alert('Payment method added successfully!');
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setClientSecret('');
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      await axios.post(`/api/billing/payment-methods/${actualTenantId}/${paymentMethodId}/default`);
      await fetchPaymentMethods();
      alert('Default payment method updated');
    } catch (err) {
      console.error('Error setting default payment method:', err);
      alert('Failed to update default payment method');
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      await axios.delete(`/api/billing/payment-methods/${actualTenantId}/${paymentMethodId}`);
      await fetchPaymentMethods();
      alert('Payment method removed');
    } catch (err) {
      console.error('Error removing payment method:', err);
      alert('Failed to remove payment method');
    }
  };

  const getCardBrandIcon = (brand) => {
    const brandLower = brand?.toLowerCase();
    switch (brandLower) {
      case 'visa':
        return '💳 Visa';
      case 'mastercard':
        return '💳 Mastercard';
      case 'amex':
        return '💳 Amex';
      case 'discover':
        return '💳 Discover';
      default:
        return '💳 Card';
    }
  };

  if (loading) {
    return <div className="payment-loading">Loading payment methods...</div>;
  }

  return (
    <div className="payment-methods" style={{ position: 'relative', paddingBottom: '72px' }}>
      <div className="payment-header">
        <h2>Payment Methods</h2>
        {!showAddForm && (
          <button className="btn-add" onClick={handleAddPaymentMethod}>
            Add Payment Method
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showAddForm && clientSecret && (
        <div className="add-payment-form">
          <h3>Add New Payment Method</h3>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              actualTenantId={actualTenantId}
              clientSecret={clientSecret}
              onSuccess={handlePaymentSuccess}
              onCancel={handleCancelAdd}
            />
          </Elements>
        </div>
      )}

      {!showAddForm && paymentMethods.length === 0 && (
        <div className="no-methods">
          <p>No payment methods saved</p>
          <p className="help-text">Add a payment method to manage your subscription</p>
        </div>
      )}

      {!showAddForm && paymentMethods.length > 0 && (
        <div className="methods-list">
          {paymentMethods.map(method => (
            <div key={method.id} className="method-card">
              <div className="method-info">
                <div className="method-brand">
                  {getCardBrandIcon(method.card_brand)}
                </div>
                <div className="method-details">
                  <div className="method-number">•••• {method.card_last4}</div>
                  <div className="method-expiry">
                    Expires {method.card_exp_month}/{method.card_exp_year}
                  </div>
                </div>
                {method.is_default === 1 && (
                  <div className="default-badge">Default</div>
                )}
              </div>
              <div className="method-actions">
                {method.is_default === 0 && (
                  <button
                    className="btn-action"
                    onClick={() => handleSetDefault(method.stripe_payment_method_id)}
                  >
                    Set as Default
                  </button>
                )}
                <button
                  className="btn-remove"
                  onClick={() => handleRemovePaymentMethod(method.stripe_payment_method_id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* App Footer */}
      <AppFooter
        buttons={[
          {
            icon: <FaReceipt />,
            label: "Billing",
            onClick: () => navigate(`/${actualTenantId}/billing`),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${actualTenantId}/dashboard`),
          },
          {
            icon: <FaCreditCard />,
            label: "Payment",
            onClick: () => navigate(`/${actualTenantId}/billing/payment-methods`),
          },
        ]}
      />
    </div>
  );
};

export default PaymentMethodForm;